import { SNAP_MINUTES } from './timelineLayout';

/**
 * Which of the day's blocks the Now screen is about, as arithmetic over plain numbers.
 *
 * Separate from the component for the reason `timelineLayout.ts` is: the answer depends on the
 * hour, and `e2e/clock.ts` argues at length that an hour-dependent precondition belongs in a unit
 * test "at the seam that already exists … a pure function of numbers" rather than behind a faked
 * browser clock, in an app whose entire domain is time. This is that seam. The e2e spec then
 * asserts only what is true at every hour, by *constructing* a block around the clock rather than
 * moving it.
 *
 * ## What the data actually looks like
 *
 * The screen is named after its `running` state, and on 2026-09-10 not one account on the
 * reference instance was in it. A sweep of all nine persona accounts across 61 days found 38
 * blocks in total, **none of them on today**, and the ADHD account the acceptance criteria names
 * — the one seeded with eleven `in_progress` tasks — has no block on any of those days at all.
 * So `next` and `none` are not the fallbacks here. They are what this screen opens in, for
 * everybody, and they are ordered and tested as the primary cases.
 *
 * ## Why the overrun question is a held id and not a window of minutes
 *
 * §3 excludes time tracking and pomodoro, and the cheapest way to smuggle a pomodoro in would be
 * a rule like "a block counts as just-ended for twenty minutes afterwards" — an interval this app
 * invented, tracked against, and would then have to explain. There is no such interval. The strip
 * asks "Done, or fifteen more minutes?" about the block it *was already showing*, which is the
 * situation the request describes ("the person is looking at the screen"); somebody arriving at
 * six in the evening is asked nothing about a block that ran out at half past nine. `heldId`
 * carries that, and it is also what `N` writes when it steps to the next block, so one piece of
 * state answers both.
 */

/** A block as `GET /api/day/:date` returns it — the fields this screen judges by, and no more. */
export interface NowBlock {
  id: string;
  title: string;
  /** ISO 8601, UTC. INV-UTC: stored as an instant, compared as an instant. */
  startsAt: string;
  endsAt: string;
  taskId: string | null;
  completedAt: string | null;
  /** Deleted in Google. The day carries it for a week so the deletion can be seen, not worked in. */
  deletedAt: string | null;
}

export type NowFocus =
  /** The clock is inside this block. */
  | { kind: 'running'; block: NowBlock; elapsed: number; leftMinutes: number }
  /** It ran out while the screen was showing it, and nobody has said what happened. */
  | { kind: 'overrun'; block: NowBlock; overMinutes: number }
  /** Nothing is running; this is what the day does next. */
  | { kind: 'next'; block: NowBlock; startsInMinutes: number }
  /** Nothing running and nothing left — the state every real account is in today. */
  | { kind: 'none' };

const live = (blocks: readonly NowBlock[]): NowBlock[] =>
  blocks.filter((b) => b.deletedAt === null);

const startOf = (b: NowBlock): number => Date.parse(b.startsAt);
const endOf = (b: NowBlock): number => Date.parse(b.endsAt);

/**
 * The block on screen, and how it stands against the clock.
 *
 * **A block the reader asked for wins, and that ordering is the whole of `N`.** The first draft
 * put `running` above everything, which read sensibly and broke the key: with a block running,
 * pressing `N` set the held id to the next block and then handed back the running one again, so
 * the key did nothing at all and there was no way to see what was coming without leaving. A held
 * block that has not yet ended is a request, and a request beats the clock — the same ruling
 * `App.tsx` makes for the full read view over a breakpoint.
 *
 * Once the held block has *ended*, the hold lapses: it becomes the overrun question if it ran out
 * while the reader was here, and otherwise the screen falls back to the unheld answer, which is
 * how the day moves on by itself when nobody is pressing anything.
 *
 * Unheld, the order is `running` → `next` → `none`.
 */
export function focusNow(input: {
  blocks: readonly NowBlock[];
  /** Milliseconds since the epoch. */
  now: number;
  /** The block this screen is already about — see the docstring above. */
  heldId?: string | null;
  /**
   * When this screen was opened, which is what "ran out under the reader" means exactly.
   *
   * Without it, arriving from a block that finished at half past nine is greeted at six in the
   * evening with "That block ended 510 min ago. Done, or fifteen more minutes?" — the way in is
   * the block bar on Today, and it hands over whichever block was selected, so that is reachable
   * rather than hypothetical. Defaults to `now`, which asks nothing: a caller that does not say
   * when it arrived cannot have watched anything run out.
   */
  openedAt?: number;
}): NowFocus {
  const { now, heldId = null, openedAt = now } = input;
  const blocks = live(input.blocks);

  const running = (block: NowBlock): NowFocus => ({
    kind: 'running',
    block,
    // A zero-length block cannot contain `now`, so the span is positive wherever this is reached.
    elapsed: (now - startOf(block)) / (endOf(block) - startOf(block)),
    leftMinutes: Math.round((endOf(block) - now) / 60_000),
  });
  const upcoming = (block: NowBlock): NowFocus => ({
    kind: 'next',
    block,
    startsInMinutes: Math.round((startOf(block) - now) / 60_000),
  });

  const held = heldId === null ? undefined : blocks.find((b) => b.id === heldId);
  if (held) {
    if (startOf(held) <= now && now < endOf(held)) return running(held);
    if (startOf(held) > now) return upcoming(held);
    // Ended. The hold lapses, but first: did it run out while the reader was here?
    if (endOf(held) >= openedAt && held.completedAt === null) {
      return {
        kind: 'overrun',
        block: held,
        overMinutes: Math.round((now - endOf(held)) / 60_000),
      };
    }
  }

  /* Nothing held, or the hold has lapsed. The latest start is the most specific claim on this
     particular minute, which is what settles an overlap. */
  const live_now = blocks.filter((b) => startOf(b) <= now && now < endOf(b));
  if (live_now.length > 0) {
    return running(live_now.reduce((best, b) => (startOf(b) > startOf(best) ? b : best)));
  }

  const ahead = blocks.filter((b) => startOf(b) > now);
  if (ahead.length > 0) {
    return upcoming(ahead.reduce((best, b) => (startOf(b) < startOf(best) ? b : best)));
  }

  return { kind: 'none' };
}

/**
 * How far ahead the Now rail will look into the next day.
 *
 * The rail reads one day page, and `GET /api/day/:date` selects blocks overlapping that calendar
 * day — so a block starting after local midnight is tomorrow's row and the rail never saw it. Late
 * evening is exactly when the next thing is on the other side of midnight, and the screen answered
 * "Nothing after this." with a block forty minutes away.
 *
 * A horizon rather than the whole of tomorrow, because merging the next day outright would put a
 * 09:00 block on the rail at 08:00 the previous morning — twenty-five hours away, which is not the
 * question the rail asks. Six hours carries the reported case and every evening like it, and is
 * short enough that nothing from tomorrow can appear before the evening it belongs to: at 17:00 the
 * horizon reaches 23:00 and stops.
 *
 * Here rather than in the component because it is a rule about what "up next" means, which is this
 * file's subject.
 */
export const RAIL_HORIZON_MS = 6 * 60 * 60 * 1000;

/**
 * What is still to come, earliest first — the rail, and what `N` steps through.
 *
 * Anything still running is included, not only what has yet to start: during an overlap the other
 * block is the thing most likely to be forgotten, which is the question this rail exists to
 * answer. What has finished is gone, because "what am I forgetting" is never about the past.
 */

export function upNext(input: {
  blocks: readonly NowBlock[];
  now: number;
  /** The block already on screen, which does not need listing beside itself. */
  excludeId: string | null;
}): NowBlock[] {
  return live(input.blocks)
    .filter((b) => b.id !== input.excludeId && endOf(b) > input.now)
    .sort((a, b) => startOf(a) - startOf(b));
}

/**
 * A block's new length after "fifteen more minutes", on the timeline's own grid.
 *
 * Rounded **up** to `SNAP_MINUTES`, never to nearest: a 50 minute block from Google has to reach
 * at least 65, and nearest would answer 60 — a `+15` that took ten minutes away. The floor is one
 * snap, so the arithmetic can never return a block shorter than the grid allows.
 */
export function extendedMinutes(currentMinutes: number, by: number, snap = SNAP_MINUTES): number {
  return Math.max(snap, Math.ceil((currentMinutes + by) / snap) * snap);
}
